// utils/encryption.ts
// Migration backend : la génération de session (One-Time Ticket + chiffrement sécurisé)
// est désormais déléguée au serveur GraphQL pour ne plus exposer de clé secrète côté client.

import apolloClient from '../config/apolloClient';
import { GET_TUNNEL_SESSION_URL } from '../graphql/TunnelSession/query';
import { SNACKBAR } from '../graphql/local/snackBar/mutation';
import AuthService from '../Services/AuthService';
import { RefreshToken } from '../Services/RefreshToken';

export interface RedirectToTunnelOptions {
  orgId?: string | number | string[];
  params?: Record<string, string | number | boolean | undefined | null>;
  path?: string;
}

// Code renvoyé par le serveur GraphQL quand la session ne permet pas de générer un lien
// tunnel valide (refresh token absent, expiré ou révoqué).
export const TUNNEL_SESSION_EXPIRED = 'TUNNEL_SESSION_EXPIRED';

export class TunnelSessionExpiredError extends Error {
  constructor() {
    super('Session expirée : reconnexion nécessaire pour accéder au tunnel');
    this.name = 'TunnelSessionExpiredError';
  }
}

const isTunnelSessionExpired = (error: unknown): boolean => {
  if ((error as Error)?.name === 'TunnelSessionExpiredError') return true;
  const errors = (error as any)?.errors || (error as any)?.graphQLErrors || [];
  return errors.some((e: any) => e?.extensions?.code === TUNNEL_SESSION_EXPIRED);
};

const toTunnelPath = (path?: string): string => {
  if (!path) return '';
  return path.startsWith('/') ? path : `/${path}`;
};

const toQuery = (options: RedirectToTunnelOptions): URLSearchParams => {
  const query = new URLSearchParams();
  const orgId = Array.isArray(options.orgId) ? options.orgId[0] : options.orgId;
  if (orgId) query.set('org', String(orgId));
  if (options.params) {
    Object.entries(options.params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') query.set(k, String(v));
    });
  }
  return query;
};

/**
 * Lien de connexion qui renvoie vers la même page du tunnel une fois reconnecté
 * (Login.tsx régénère alors le lien avec la nouvelle session).
 */
export const buildTunnelLoginUrl = (options: RedirectToTunnelOptions = {}): string => {
  const qs = toQuery(options).toString();
  const redirectTunnel = `${toTunnelPath(options.path) || '/'}${qs ? `?${qs}` : ''}`;
  return `/auth/login?${new URLSearchParams({ redirect_tunnel: redirectTunnel }).toString()}`;
};

export const getTunnelSessionUrl = async (
  options: RedirectToTunnelOptions = {},
): Promise<string> => {
  const { orgId, params, path } = options;
  const resolvedOrgId = Array.isArray(orgId) ? orgId[0] : orgId;
  const refreshTokenData = RefreshToken.getTRefreshToken();
  const refreshToken = refreshTokenData?.refreshToken;

  // Sans refresh token (ex. connexion par jeton dans l'URL), le tunnel rejetterait le lien.
  if (!refreshToken) {
    throw new TunnelSessionExpiredError();
  }

  const result = await apolloClient.query<{
    getTunnelSessionUrl: {
      url: string;
      iv: string;
      auth: string;
      ticket: string;
    };
  }>({
    query: GET_TUNNEL_SESSION_URL,
    variables: {
      orgId:
        resolvedOrgId !== undefined && resolvedOrgId !== null ? String(resolvedOrgId) : undefined,
      params: params ? JSON.stringify(params) : undefined,
      path: path || '',
      refreshToken,
    },
    fetchPolicy: 'network-only',
  });

  const url = result?.data?.getTunnelSessionUrl?.url;
  if (!url) {
    throw new Error('URL de session du tunnel non retournée par le serveur');
  }
  return url;
};

export const redirectToTunnel = async (options: RedirectToTunnelOptions = {}): Promise<void> => {
  try {
    const url = await getTunnelSessionUrl(options);
    window.location.href = url;
  } catch (error) {
    console.error('Erreur lors de la redirection sécurisée vers le tunnel:', error);

    // Session inutilisable pour le tunnel : reconnexion, puis retour automatique au tunnel.
    // Ne jamais envoyer le client sur le tunnel sans authentification valide : il y reste bloqué.
    if (isTunnelSessionExpired(error)) {
      AuthService.clearAllSessionData();
      window.location.href = buildTunnelLoginUrl(options);
      return;
    }

    // Erreur passagère (réseau, API) : le client reste sur l'espace client et peut réessayer.
    apolloClient
      .mutate({
        mutation: SNACKBAR,
        variables: {
          isOpen: true,
          message: "Impossible d'ouvrir le formulaire pour le moment, veuillez réessayer.",
          type: 'ERROR',
          duration: 6000,
        },
      })
      .catch(() => {});
  }
};
